import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { procurementService } from '../../../../services/procurementService';
import type { LegalEntity, UpsertLegalEntityRequest } from '../../../../types/procurement';
import { EditModal, FormField, inputClasses } from './EditModal';

export const LegalEntitySection: React.FC = () => {
  const [rows, setRows] = useState<LegalEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; entity: UpsertLegalEntityRequest; originalNo?: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    procurementService
      .getLegalEntities()
      .then(setRows)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const empty = (): UpsertLegalEntityRequest => ({
    legalEntityNo: '',
    legalEntityName: '',
    delAddress1: '',
    delAddress2: '',
    delAddress3: '',
    delZipCode: '',
    delCity: '',
    contactName: '',
  });

  const handleSave = async () => {
    if (!editing) return;
    const e = editing.entity;
    if (!e.legalEntityNo.trim() || !e.legalEntityName.trim()) {
      setError('Selskapsnummer og navn er påkrevd');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editing.mode === 'create') {
        await procurementService.createLegalEntity(e);
      } else if (editing.originalNo) {
        await procurementService.updateLegalEntity(editing.originalNo, e);
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ukjent feil');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: LegalEntity) => {
    if (!window.confirm(`Slette selskap ${row.legalEntityNo} – ${row.legalEntityName}?`)) return;
    try {
      await procurementService.deleteLegalEntity(row.legalEntityNo);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Kunne ikke slette');
    }
  };

  const field = <K extends keyof UpsertLegalEntityRequest>(key: K, value: UpsertLegalEntityRequest[K]) => {
    if (!editing) return;
    setEditing({ ...editing, entity: { ...editing.entity, [key]: value } });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Selskap (Legal Entity)</h2>
        <button
          onClick={() => setEditing({ mode: 'create', entity: empty() })}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-3 py-1.5 rounded flex items-center gap-2 text-sm"
        >
          <Plus size={16} /> Nytt selskap
        </button>
      </div>

      {error && !editing && (
        <div className="m-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={18} /> Laster…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">Ingen selskap registrert.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-2 text-left">Nr.</th>
              <th className="px-4 py-2 text-left">Navn</th>
              <th className="px-4 py-2 text-left">Adresse</th>
              <th className="px-4 py-2 text-left">Poststed</th>
              <th className="px-4 py-2 text-left">Kontakt</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.legalEntityNo} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{r.legalEntityNo}</td>
                <td className="px-4 py-2">{r.legalEntityName}</td>
                <td className="px-4 py-2 text-gray-700">{[r.delAddress1, r.delAddress2, r.delAddress3].filter(Boolean).join(', ') || '—'}</td>
                <td className="px-4 py-2 text-gray-700">{[r.delZipCode, r.delCity].filter(Boolean).join(' ') || '—'}</td>
                <td className="px-4 py-2 text-gray-700">{r.contactName || '—'}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setEditing({ mode: 'edit', entity: {
                      legalEntityNo: r.legalEntityNo, legalEntityName: r.legalEntityName,
                      delAddress1: r.delAddress1 ?? '', delAddress2: r.delAddress2 ?? '',
                      delAddress3: r.delAddress3 ?? '', delZipCode: r.delZipCode ?? '',
                      delCity: r.delCity ?? '', contactName: r.contactName ?? ''
                    }, originalNo: r.legalEntityNo })}
                    className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 mr-1"
                    aria-label="Rediger"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(r)}
                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                    aria-label="Slett"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <EditModal
          title={editing.mode === 'create' ? 'Nytt selskap' : `Rediger selskap ${editing.originalNo}`}
          onClose={() => { setEditing(null); setError(null); }}
          onSave={handleSave}
          saving={saving}
          error={error}
          canSave={editing.entity.legalEntityNo.trim().length > 0 && editing.entity.legalEntityName.trim().length > 0}
        >
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Selskapsnummer" required>
              <input
                type="text" maxLength={3}
                disabled={editing.mode === 'edit'}
                value={editing.entity.legalEntityNo}
                onChange={(e) => field('legalEntityNo', e.target.value)}
                className={`${inputClasses} ${editing.mode === 'edit' ? 'bg-gray-100' : ''}`}
              />
            </FormField>
            <FormField label="Selskapsnavn" required>
              <input type="text" value={editing.entity.legalEntityName}
                onChange={(e) => field('legalEntityName', e.target.value)} className={inputClasses} />
            </FormField>
          </div>

          <FormField label="Leveringsadresse 1">
            <input type="text" value={editing.entity.delAddress1 ?? ''}
              onChange={(e) => field('delAddress1', e.target.value)} className={inputClasses} />
          </FormField>
          <FormField label="Leveringsadresse 2">
            <input type="text" value={editing.entity.delAddress2 ?? ''}
              onChange={(e) => field('delAddress2', e.target.value)} className={inputClasses} />
          </FormField>
          <FormField label="Leveringsadresse 3">
            <input type="text" value={editing.entity.delAddress3 ?? ''}
              onChange={(e) => field('delAddress3', e.target.value)} className={inputClasses} />
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Postnummer">
              <input type="text" value={editing.entity.delZipCode ?? ''}
                onChange={(e) => field('delZipCode', e.target.value)} className={inputClasses} />
            </FormField>
            <FormField label="Poststed">
              <input type="text" value={editing.entity.delCity ?? ''}
                onChange={(e) => field('delCity', e.target.value)} className={inputClasses} />
            </FormField>
          </div>

          <FormField label="Kontaktperson">
            <input type="text" value={editing.entity.contactName ?? ''}
              onChange={(e) => field('contactName', e.target.value)} className={inputClasses} />
          </FormField>
        </EditModal>
      )}
    </div>
  );
};
